// Точка входа PWA: состояние, hash-роутер, делегирование событий, занятие.
// Данные — снимок сервера из IndexedDB плюс локальная очередь событий (sync.ts).

import { ApiError, httpApi, NetworkError } from './api.ts';
import { uuid } from './ids.ts';
import { play, stopAudio } from './audio.ts';
import { cachedAudioKeys, clearAudioCache, syncAudioCache } from './audiocache.ts';
import {
  audioKeysToCache, buildOverlay, countPlay, flagEvent, genPollDelay, listenQueue, listenReviewEvent, mainExample, newListenCard,
  MAX_LISTEN, normalizeUnitInput, pickNew, plural, pushView, textQueue, validatePushSettings, validateUnitInput, type AudioAvailability, type DictFilter,
} from './logic.ts';
import { deviceSubscription, notificationPermission, pushSupported, subscribeDevice } from './push.ts';
import { canSpeak, onVoicesChanged } from './speech.ts';
import { openStore } from './store.ts';
import { startAutoSync, Sync } from './sync.ts';
import type { LearningEvent, PoolOrder, Problem, Rating, Reason, Settings, Snapshot, UnitInput } from './types.ts';
import {
  addView, bootView, GEN_OFF_NOTICE, detailView, dictList, dictView, doneView, emptyUnitInput, introView, listenCardView, loginView, progressView,
  reviewView, settingsView, textCardView, todayView, unitToInput, type DraftInput, type FormState, type ReviewState, type Route,
  type Session, type SetsState, type View,
} from './views.ts';

const root = document.getElementById('app')!;

const form = <T>(): FormState<T> => ({ draft: null, errors: {}, message: '', busy: false });
function reviewState(): ReviewState {
  return { editing: false, confirm: '', busy: false, message: '' };
}

const S = {
  route: { name: 'today' } as Route,
  online: navigator.onLine,
  needLogin: false,
  login: { error: '', busy: false, email: '' },
  dict: { filter: 'all' as DictFilter, q: '' },
  session: null as Session | null,
  detail: { editing: false, fromSession: false },
  add: form<UnitInput>(),
  draft: form<DraftInput>(),
  edit: form<UnitInput>(),
  review: reviewState(),
  sets: { confirm: '', busy: '', message: '' } as SetsState,
  settings: form<Settings>(),
  /** Уведомления: есть ли подписка у этого устройства (null — ещё не прочитано), запросы, сообщение. */
  push: { subscribed: null as boolean | null, busy: false, testBusy: false, message: '' },
  /** Ключи записей, сохранённых на устройстве (кэш audio-v1). */
  cachedAudio: new Set<string>(),
};

let sync: Sync;

// ——— роутер ———

function parseHash(): Route {
  const h = location.hash.replace(/^#\/?/, '');
  const [name, id] = h.split('/');
  switch (name) {
    case 'dict': case 'settings': case 'progress': case 'session':
      return { name };
    case 'add':
      return id === 'manual' ? { name: 'add', manual: true } : { name: 'add' };
    case 'unit':
      return id ? { name: 'unit', id: decodeURIComponent(id) } : { name: 'dict' };
    case 'review':
      return id ? { name: 'review', id: decodeURIComponent(id) } : { name: 'dict' };
    default:
      return { name: 'today' };
  }
}

function go(path: string) {
  if (location.hash === '#/' + path) render();
  else location.hash = '#/' + path;
}

function onRoute() {
  const prev = S.route;
  const next = parseHash();
  stopAudio();
  if (next.name === 'session' && !S.session) return go('today');
  if (next.name !== 'session' && S.session?.kind === 'done') S.session = null;
  if (next.name === 'unit' && !(prev.name === 'unit' && prev.id === next.id)) {
    // Возврат к занятию возможен, только если подробный вид открыт с карточки.
    S.detail = { editing: false, fromSession: prev.name === 'session' && !!S.session };
    S.edit = form();
    if (sync.snapshot?.units.some((u) => u.id === next.id)) void logEvent({ type: 'detail_view', unitId: next.id });
  }
  if (next.name === 'review' && !(prev.name === 'review' && prev.id === next.id)) {
    // Экран проверки — не подробный вид: detail_view не пишется.
    S.review = reviewState();
    S.edit = form();
  }
  if (next.name === 'add' && !(prev.name === 'add' && prev.manual === next.manual)) {
    S.add = form();
    S.draft = form();
    S.sets = { confirm: '', busy: '', message: '' };
  }
  // Открытие словаря обновляет снимок, если какая-то фраза ещё готовится (опрос мог закончиться).
  if (next.name === 'dict' && prev.name !== 'dict' && genPollDelay(sync.snapshot?.units ?? [], 0) !== null) pollGeneration();
  if (next.name === 'settings' && prev.name !== 'settings') {
    S.settings = form();
    S.push.message = '';
    void refreshPush();
  }
  S.route = next;
  render();
  window.scrollTo(0, 0);
}

// ——— рендер ———

function view(): View {
  return {
    snap: sync.snapshot!,
    overlay: buildOverlay(sync.localEvents()),
    now: new Date(),
    online: S.online,
    canSpeak: canSpeak(),
    audio: audioAvailability(),
    pending: sync.pendingCount(),
    syncStatus: sync.status,
    dict: S.dict,
    session: S.session,
    detail: S.detail,
    add: S.add,
    draft: S.draft,
    edit: S.edit,
    review: S.review,
    sets: S.sets,
    settings: S.settings,
    push: {
      ...pushView({ supported: pushSupported(), serverEnabled: !!sync.snapshot?.push?.enabled, permission: notificationPermission(),
        subscribed: S.push.subscribed, online: S.online && sync.status !== 'offline' }),
      busy: S.push.busy, testBusy: S.push.testBusy, message: S.push.message,
    },
  };
}

/** Записи доступны по сети, если она есть и сервер отвечает; иначе — только из кэша устройства. */
function audioAvailability(): AudioAvailability {
  return { online: S.online && sync.status !== 'offline', cached: S.cachedAudio };
}

function screenHtml(): string {
  if (S.needLogin) return loginView(S.login.error, S.login.busy, S.login.email);
  if (!sync.snapshot) return bootView(sync.status === 'offline');
  const v = view();
  const r = S.route;
  switch (r.name) {
    case 'today': return todayView(v);
    case 'dict': return dictView(v);
    case 'add': return addView(v, r.manual);
    case 'unit': return detailView(v, r.id);
    case 'review': return reviewView(v, r.id);
    case 'settings': return settingsView(v);
    case 'progress': return progressView();
    case 'session': {
      const s = S.session!;
      return s.kind === 'intro' ? introView(v, s) : s.kind === 'text' ? textCardView(v, s)
        : s.kind === 'listen' ? listenCardView(v, s) : doneView(s);
    }
  }
}

/** Перерисовка с сохранением фокуса и позиции курсора (поиск, формы). */
function render() {
  const a = document.activeElement as HTMLInputElement | null;
  const key = a && root.contains(a) ? (a.id ? `#${a.id}` : a.name ? `[name="${a.name}"]` : '') : '';
  const sel = key && 'selectionStart' in a! ? [a!.selectionStart, a!.selectionEnd] : null;
  const scroll = root.querySelector('.screen')?.scrollTop ?? 0;
  root.innerHTML = screenHtml();
  const scr = root.querySelector('.screen');
  if (scr) scr.scrollTop = scroll;
  if (key) {
    const el = root.querySelector<HTMLInputElement>(key);
    if (el) {
      el.focus();
      if (sel && sel[0] != null) try { el.setSelectionRange(sel[0], sel[1]); } catch { /* не текстовое поле */ }
    }
  }
}

// ——— события обучения ———

async function logEvent(e: Omit<LearningEvent, 'id' | 'time'> & { id?: string }) {
  await sync.enqueue({ ...e, id: e.id ?? uuid(), time: new Date().toISOString() });
}

function newCard() {
  return { eventId: uuid(), flipped: false, spoiler: false, plays: 0 };
}

function startText(unitIds: string[], first: boolean) {
  if (!unitIds.length) return;
  S.session = { kind: 'text', first, unitIds, i: 0, card: newCard() };
  go('session');
}

/** Аудиокарточка единицы: случайный пригодный пример выбирается один раз, при показе. */
function listenCard(unitId: string) {
  const u = sync.snapshot!.units.find((x) => x.id === unitId);
  return newListenCard(uuid(), u, buildOverlay(sync.localEvents()), audioAvailability());
}

function startListen() {
  const snap = sync.snapshot!;
  const ids = listenQueue(snap.units, buildOverlay(sync.localEvents()), new Date(), audioAvailability()).map((u) => u.id);
  if (!ids.length) return;
  S.session = { kind: 'listen', unitIds: ids, i: 0, card: listenCard(ids[0]) };
  go('session');
}

function startIntro() {
  const snap = sync.snapshot!;
  const picked = pickNew(snap.units, buildOverlay(sync.localEvents()), snap.settings);
  if (!picked.length) return;
  S.session = { kind: 'intro', unitIds: picked.map((u) => u.id), i: 0, eventId: uuid(), introduced: [] };
  go('session');
}

async function introNext(i: number) {
  const s = S.session;
  if (s?.kind !== 'intro' || s.i !== i) return; // повторное нажатие на ту же фразу
  const unitId = s.unitIds[i];
  const u = sync.snapshot!.units.find((x) => x.id === unitId);
  const eventId = s.eventId;
  s.introduced.push(unitId);
  if (i < s.unitIds.length - 1) {
    s.i++;
    s.eventId = uuid();
    stopAudio();
    render();
  } else {
    stopAudio();
    startText(s.introduced, true);
  }
  if (u) await logEvent({ id: eventId, type: 'intro', unitId, exampleId: mainExample(u)?.id });
}

async function rate(i: number, rating: Rating) {
  const s = S.session;
  if (s?.kind !== 'text' || s.i !== i || !s.card.flipped) return; // двойное нажатие
  const unitId = s.unitIds[i];
  const u = sync.snapshot!.units.find((x) => x.id === unitId);
  const c = s.card;
  advanceCard(s);
  if (u) {
    await logEvent({ id: c.eventId, type: 'review', unitId, exampleId: mainExample(u)?.id, track: 'text',
      rating, plays: c.plays, spoilerOpened: c.spoiler });
  }
}

function advanceCard(s: Extract<Session, { kind: 'text' | 'listen' }>) {
  stopAudio();
  if (s.i < s.unitIds.length - 1) {
    s.i++;
    if (s.kind === 'text') s.card = newCard();
    else s.card = listenCard(s.unitIds[s.i]);
    render();
  } else {
    S.session = { kind: 'done', first: s.kind === 'text' && s.first, count: s.unitIds.length };
    render();
  }
}

/** Оценка аудиокарточки или «Отложить до исправления»; повторное нажатие по пройденной карточке игнорируется. */
async function listenFinish(i: number, rating: Rating | null) {
  const s = S.session;
  if (s?.kind !== 'listen' || s.i !== i || !s.card.flipped || (rating === null) !== s.card.flag) return;
  const u = sync.snapshot!.units.find((x) => x.id === s.unitIds[i]);
  const c = s.card;
  advanceCard(s);
  if (!u) return;
  const time = new Date().toISOString();
  await sync.enqueue(rating ? listenReviewEvent(c, u, rating, time) : flagEvent(c, u, time));
}

function listenPlay(i: number) {
  const s = S.session;
  if (s?.kind !== 'listen' || s.i !== i) return;
  const key = s.card.example?.audio;
  if (!key) return;
  countPlay(s.card);
  // Без запасного голоса ОС: аудиокарточка играет только запись.
  play({ key, rate: s.card.slow ? 0.75 : 1 }, false);
  render();
}

// ——— записи на устройстве ———

let cacheRun: Promise<void> | null = null;
let cacheAgain = false;
let cachedFor: Snapshot | null = null;

/** После каждого нового снимка: докачать записи ближайших дней и следующего знакомства, удалить ненужные. */
function scheduleAudioCache() {
  const snap = sync.snapshot;
  if (!snap || snap === cachedFor) return;
  if (cacheRun) { cacheAgain = true; return; }
  cachedFor = snap;
  const need = audioKeysToCache(snap.units, buildOverlay(sync.localEvents()), snap.settings, new Date());
  cacheRun = syncAudioCache(need)
    .then((have) => { S.cachedAudio = have; })
    .catch((e) => console.warn('audio cache', e))
    .finally(() => {
      cacheRun = null;
      render();
      if (cacheAgain) { cacheAgain = false; scheduleAudioCache(); }
    });
}

// ——— формы ———

function readUnitForm(f: HTMLFormElement): UnitInput {
  const fd = new FormData(f);
  const g = (k: string) => String(fd.get(k) ?? '');
  return {
    kind: g('kind') as UnitInput['kind'], form: g('form'), meaning: g('meaning'), senseLabel: g('senseLabel'),
    explanation: g('explanation'), sourceNote: g('sourceNote'),
    main: { en: g('main.en'), ru: g('main.ru'), target: g('main.target') },
    listen: Array.from({ length: MAX_LISTEN }, (_, i) => ({
      en: g(`listen.${i + 1}.en`), ru: g(`listen.${i + 1}.ru`), target: g(`listen.${i + 1}.target`),
    })),
  };
}

function readSettingsForm(f: HTMLFormElement): Settings {
  const fd = new FormData(f);
  const num = (k: string) => {
    const raw = String(fd.get(k) ?? '').trim();
    return raw === '' ? NaN : Number(raw);
  };
  return {
    newPerSession: num('newPerSession'), poolOrder: String(fd.get('poolOrder') ?? 'own_first') as PoolOrder,
    quietStart: String(fd.get('quietStart') ?? ''), quietEnd: String(fd.get('quietEnd') ?? ''), maxPushPerDay: num('maxPushPerDay'),
    timeZone: deviceTimeZone(),
  };
}

/** Часовой пояс устройства: по нему сервер считает тихие часы и сутки для лимита. */
function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

function apiFailure(e: unknown, st: { errors?: Record<string, string>; message: string }) {
  if (e instanceof ApiError && e.status === 422) {
    st.errors = e.fields;
    st.message = 'Проверьте отмеченные поля';
  } else if (e instanceof ApiError && e.status === 401) {
    st.message = '';
    requireLogin();
  } else if (e instanceof ApiError && e.status === 404) {
    st.message = 'Фраза не найдена на сервере';
  } else if (e instanceof NetworkError) {
    st.message = 'Нет связи с сервером. Попробуйте позже.';
  } else {
    st.message = e instanceof Error ? e.message : 'Ошибка';
  }
}

/** Фраза для генерации: сразу в словарь со статусом «готовится», затем опрос снимка. */
async function submitDraft(f: HTMLFormElement) {
  const st = S.draft;
  if (st.busy) return;
  const fd = new FormData(f);
  st.draft = { form: String(fd.get('form') ?? ''), sourceNote: String(fd.get('sourceNote') ?? '') };
  st.errors = st.draft.form.trim() ? {} : { form: 'Обязательное поле' };
  st.message = '';
  if (Object.keys(st.errors).length) return render();
  st.busy = true;
  render();
  try {
    const u = await httpApi.createDraft(st.draft.form, st.draft.sourceNote);
    await sync.upsertUnit(u);
    S.draft = form();
    S.dict = { filter: 'all', q: '' };
    go('dict');
    pollGeneration(true);
  } catch (e) {
    st.busy = false;
    if (e instanceof ApiError && e.status === 409 && sync.snapshot) {
      // Сервер перезапущен без генерации, а снимок ещё старый: ручная форма с введёнными полями.
      sync.snapshot.generation = false;
      const { form: phrase, sourceNote } = st.draft;
      S.add = { ...form(), draft: { ...emptyUnitInput(), form: phrase, sourceNote }, message: GEN_OFF_NOTICE };
      S.draft = form();
    } else apiFailure(e, st);
    render();
  }
}

let pollTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Генерация идёт на сервере в фоне: пока есть фразы «готовится», обновляем снимок с растущей паузой
 * (2, 5, 10, 20, 40 с). Когда генерация закончилась — ждём записи для экрана проверки.
 */
function pollGeneration(restart = false, step = 0) {
  if (pollTimer) {
    if (!restart) return;
    clearTimeout(pollTimer);
  }
  pollTimer = null;
  const delay = genPollDelay(sync.snapshot?.units ?? [], step);
  if (delay === null) {
    for (const u of sync.snapshot?.units ?? []) if (u.status === 'review') awaitRecordings(u.id);
    return;
  }
  pollTimer = setTimeout(async () => {
    pollTimer = null;
    if (S.online) await sync.syncNow();
    pollGeneration(false, step + 1);
  }, delay);
}

async function submitUnit(kind: 'add' | 'edit', f: HTMLFormElement) {
  const st = kind === 'add' ? S.add : S.edit;
  if (st.busy) return;
  st.draft = readUnitForm(f);
  st.errors = validateUnitInput(st.draft);
  st.message = Object.keys(st.errors).length ? 'Проверьте отмеченные поля' : '';
  if (st.message) return render();
  st.busy = true;
  render();
  try {
    const input = normalizeUnitInput(st.draft);
    const r = S.route;
    const id = r.name === 'unit' || r.name === 'review' ? r.id : '';
    const u = kind === 'add' ? await httpApi.createUnit(input) : await httpApi.updateUnit(id, input);
    await sync.upsertUnit(u);
    awaitRecordings(u.id);
    st.busy = false;
    if (kind === 'add') {
      S.add = form();
      S.dict = { filter: 'all', q: '' };
      go('dict');
    } else if (r.name === 'review') {
      // «Заполнить вручную» после сбоя переводит фразу в пул; правка на проверке оставляет её там.
      S.edit = form();
      S.review = reviewState();
      if (u.status === 'pool') {
        S.dict = { filter: 'all', q: '' };
        go('dict');
      } else render();
    } else {
      S.edit = form();
      S.detail.editing = false;
      render();
    }
  } catch (e) {
    st.busy = false;
    apiFailure(e, st);
    render();
  }
}

/**
 * Озвучка идёт на сервере в фоне: ответ на сохранение приходит без записей.
 * Несколько раз обновляем снимок, пока у фразы не появятся записи (обычно за пару секунд).
 */
function awaitRecordings(unitId: string, delays = [2000, 5000, 15000]) {
  const ready = () => {
    const u = sync.snapshot?.units.find((x) => x.id === unitId);
    return !u || (!!u.phraseAudio && u.examples.every((e) => e.audio));
  };
  const [d, ...rest] = delays;
  if (d === undefined || ready()) return;
  setTimeout(async () => {
    if (!S.online) return;
    await sync.syncNow();
    awaitRecordings(unitId, rest);
  }, d);
}

// ——— наборы ———

/** Включение или выключение набора; затем новый снимок — пул «из наборов» на «Сегодня» обновится. */
async function toggleSet(id: string, on: boolean) {
  const st = S.sets;
  if (st.busy) return;
  st.busy = id;
  st.confirm = '';
  st.message = '';
  render();
  try {
    await httpApi.setEnabled(id, on);
    await sync.syncNow();
    if (on) awaitSetRecordings();
  } catch (e) {
    apiFailure(e, st);
  }
  st.busy = '';
  render();
}

/** Записи текстов набора обычно готовы с публикации; если нет — дождаться их, как после добавления фразы. */
function awaitSetRecordings() {
  const u = sync.snapshot?.units.find((x) => x.setId && (!x.phraseAudio || x.examples.some((e) => !e.audio)));
  if (u) awaitRecordings(u.id);
}

// ——— экран проверки ———

function reviewUnit() {
  const r = S.route;
  return r.name === 'review' ? sync.snapshot?.units.find((x) => x.id === r.id) : undefined;
}

/** Действие на экране проверки: запрос к серверу, затем обновление локального снимка. */
async function reviewAction(act: 'regen' | 'approve' | 'delete') {
  const u = reviewUnit();
  const r = S.review;
  if (!u || r.busy) return;
  r.busy = true;
  r.confirm = '';
  r.message = '';
  render();
  try {
    if (act === 'delete') {
      await httpApi.deleteUnit(u.id);
      await sync.removeUnit(u.id);
      S.review = reviewState();
      go('dict');
      return;
    }
    const got = act === 'regen' ? await httpApi.generate(u.id) : await httpApi.approve(u.id);
    await sync.upsertUnit(got);
    S.review = reviewState();
    if (act === 'approve') {
      S.dict = { filter: 'all', q: '' };
      go('dict');
    } else {
      render();
      pollGeneration(true);
    }
  } catch (e) {
    r.busy = false;
    if (e instanceof ApiError && e.status === 409) {
      // Состояние фразы на сервере уже другое (например, открыта старая версия) — обновляем снимок.
      r.message = 'Фраза уже изменилась, данные обновлены.';
      await sync.syncNow();
    } else apiFailure(e, r);
    render();
  }
}

function reviewEdit() {
  const u = reviewUnit();
  if (!u) return;
  // После сбоя карточки нет: ручная форма с подставленными фразой и «где встретили».
  const draft = u.status === 'gen_failed' ? { ...emptyUnitInput(), form: u.form, sourceNote: u.sourceNote } : unitToInput(u);
  S.edit = { ...form(), draft };
  S.review = { ...reviewState(), editing: true };
  render();
}

async function submitSettings(f: HTMLFormElement) {
  const st = S.settings;
  if (st.busy) return;
  st.draft = readSettingsForm(f);
  const n = st.draft.newPerSession;
  st.errors = validatePushSettings({ quietStart: st.draft.quietStart ?? '', quietEnd: st.draft.quietEnd ?? '', maxPushPerDay: st.draft.maxPushPerDay ?? NaN });
  if (!(Number.isInteger(n) && n >= 0 && n <= 20)) st.errors.newPerSession = 'Допустимо от 0 до 20';
  st.message = '';
  if (Object.keys(st.errors).length) return render();
  st.busy = true;
  render();
  try {
    const saved = await httpApi.updateSettings(st.draft);
    await sync.setSettings(saved);
    S.settings = { ...form(), message: 'Сохранено' };
  } catch (e) {
    st.busy = false;
    apiFailure(e, st);
  }
  render();
}

// ——— уведомления ———

/** Прочитать подписку устройства (переключатель в «Настройках»). */
async function refreshPush() {
  try {
    S.push.subscribed = !!(await deviceSubscription());
  } catch (e) {
    console.warn('push subscription', e);
    S.push.subscribed = false;
  }
  render();
}

function pushFailure(e: unknown) {
  if (e instanceof ApiError && e.status === 401) requireLogin();
  else if (e instanceof ApiError && e.status === 409) S.push.message = 'На сервере уведомления не настроены.';
  else if (e instanceof NetworkError) S.push.message = 'Нет связи с сервером. Попробуйте позже.';
  else S.push.message = e instanceof Error ? `Не удалось: ${e.message}` : 'Не удалось включить уведомления';
}

/**
 * Включение: разрешение браузера запрашивается сразу в обработчике нажатия (так требует Safari),
 * затем подписка устройства, её сохранение на сервере и часовой пояс в настройках.
 */
async function pushOn() {
  const key = sync.snapshot?.push?.publicKey;
  if (!key || S.push.busy) return;
  S.push.busy = true;
  S.push.message = '';
  render();
  let sub: PushSubscription | null = null;
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
      // «Запрещено» объясняет переключатель; закрытый без ответа запрос — здесь.
      if (perm === 'default') S.push.message = 'Разрешение не дано — уведомления остались выключены.';
      return;
    }
    sub = await subscribeDevice(key);
    await httpApi.subscribePush(sub.toJSON());
    S.push.subscribed = true;
    await saveTimeZone();
  } catch (e) {
    // Сервер подписку не сохранил — снимаем и на устройстве, чтобы переключатель не врал.
    if (sub && !S.push.subscribed) await sub.unsubscribe().catch(() => false);
    pushFailure(e);
  } finally {
    S.push.busy = false;
    render();
  }
}

/** Выключение: сначала удалить подписку на сервере, затем на устройстве. */
async function pushOff() {
  if (S.push.busy) return;
  S.push.busy = true;
  S.push.message = '';
  render();
  try {
    const sub = await deviceSubscription();
    if (sub) {
      await httpApi.unsubscribePush(sub.endpoint);
      await sub.unsubscribe();
    }
    S.push.subscribed = false;
  } catch (e) {
    pushFailure(e);
  } finally {
    S.push.busy = false;
    render();
  }
}

/** Часовой пояс устройства — в настройки на сервере, если он ещё не тот. */
async function saveTimeZone() {
  const cur = sync.snapshot?.settings;
  const tz = deviceTimeZone();
  if (!cur || !tz || cur.timeZone === tz) return;
  await sync.setSettings(await httpApi.updateSettings({ ...cur, timeZone: tz }));
}

async function pushTest() {
  if (S.push.testBusy) return;
  S.push.testBusy = true;
  S.push.message = '';
  render();
  try {
    const { delivered } = await httpApi.testPush();
    S.push.message = delivered ? `Отправлено на ${delivered} ${plural(delivered, ['устройство', 'устройства', 'устройств'])}. Уведомление придёт через несколько секунд.`
      : 'Не доставлено ни на одно устройство. Выключите и снова включите уведомления.';
  } catch (e) {
    pushFailure(e);
  } finally {
    S.push.testBusy = false;
    render();
  }
}

/** При выходе: чужому пользователю на этом устройстве уведомления приходить не должны. */
async function dropDeviceSubscription() {
  const sub = await deviceSubscription().catch(() => null);
  if (!sub) return;
  await httpApi.unsubscribePush(sub.endpoint).catch((e) => console.warn('push unsubscribe', e));
  await sub.unsubscribe().catch(() => false);
  S.push.subscribed = false;
}

async function submitLogin(f: HTMLFormElement) {
  if (S.login.busy) return;
  const fd = new FormData(f);
  const email = String(fd.get('email') ?? '').trim();
  const password = String(fd.get('password') ?? '');
  S.login = { error: '', busy: true, email };
  render();
  try {
    await httpApi.login(email, password);
    S.login = { error: '', busy: false, email };
    S.needLogin = false;
    await sync.syncNow(); // сначала уходят накопленные события, затем загружается снимок
    go('today');
  } catch (e) {
    const msg = e instanceof ApiError ? e.message : e instanceof NetworkError ? 'Нет связи с сервером' : 'Ошибка входа';
    S.login = { error: msg, busy: false, email };
    render();
  }
}

async function logout() {
  await dropDeviceSubscription();
  try {
    await httpApi.logout();
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 401)) {
      S.settings.message = 'Не удалось выйти: нет связи с сервером';
      return render();
    }
  }
  S.session = null;
  await sync.resetAfterLogout();
  await clearAudioCache();
  S.cachedAudio = new Set();
  cachedFor = null;
  requireLogin();
}

function requireLogin() {
  S.needLogin = true;
  S.login.busy = false;
  render();
}

// ——— делегирование ———

root.addEventListener('click', (ev) => {
  const t = ev.target as HTMLElement;
  const g = t.closest<HTMLElement>('[data-go]');
  if (g) {
    go(g.dataset.go!);
    return;
  }
  const a = t.closest<HTMLElement>('[data-act]');
  if (!a || (a as HTMLButtonElement).disabled) return;
  const s = S.session;
  switch (a.dataset.act) {
    case 'say':
      play({ key: a.dataset.k || null, fallbackText: a.dataset.t ?? '' }, canSpeak());
      if (s?.kind === 'text' && S.route.name === 'session') s.card.plays++;
      break;
    case 'startListen': startListen(); break;
    case 'listenPlay': listenPlay(Number(a.dataset.i)); break;
    case 'slow': if (s?.kind === 'listen') { s.card.slow = !s.card.slow; render(); } break;
    case 'reason':
      if (s?.kind === 'listen') {
        const r = a.dataset.r as Reason;
        s.card.reasons = s.card.reasons.includes(r) ? s.card.reasons.filter((x) => x !== r) : [...s.card.reasons, r];
        render();
      }
      break;
    case 'flagToggle': if (s?.kind === 'listen') { s.card.flag = !s.card.flag; render(); } break;
    case 'problem':
      if (s?.kind === 'listen') {
        const p = a.dataset.p as Problem;
        s.card.problem = s.card.problem === p ? '' : p;
        render();
      }
      break;
    case 'listenRate': void listenFinish(Number(a.dataset.i), a.dataset.r as Rating); break;
    case 'flagSubmit': void listenFinish(Number(a.dataset.i), null); break;
    case 'startText': {
      const snap = sync.snapshot!;
      startText(textQueue(snap.units, buildOverlay(sync.localEvents()), new Date()).map((u) => u.id), false);
      break;
    }
    case 'startIntro': startIntro(); break;
    case 'introNext': void introNext(Number(a.dataset.i)); break;
    case 'spoiler': if (s?.kind === 'text') { s.card.spoiler = true; render(); } break;
    case 'flip': if (s?.kind === 'text' || s?.kind === 'listen') { s.card.flipped = true; render(); } break;
    case 'rate': void rate(Number(a.dataset.i), a.dataset.r as Rating); break;
    case 'skipCard': if ((s?.kind === 'text' || s?.kind === 'listen') && s.i === Number(a.dataset.i)) advanceCard(s); break;
    case 'openDetail': go('unit/' + encodeURIComponent(a.dataset.id!)); break;
    case 'backToSession': go(S.session ? 'session' : 'dict'); break;
    case 'quit': stopAudio(); S.session = null; go('today'); break;
    case 'finish': S.session = null; go('today'); break;
    case 'filter': S.dict.filter = a.dataset.f as DictFilter; render(); break;
    case 'edit': {
      const r = S.route;
      const u = r.name === 'unit' ? sync.snapshot?.units.find((x) => x.id === r.id) : undefined;
      if (u) { S.edit = { ...form(), draft: unitToInput(u) }; S.detail.editing = true; render(); }
      break;
    }
    case 'cancelEdit': S.detail.editing = false; S.review.editing = false; S.edit = form(); render(); break;
    case 'reviewEdit': reviewEdit(); break;
    case 'reviewRegen': {
      const u = reviewUnit();
      // Правки на проверке теряются только после явного подтверждения.
      if (u?.status === 'review' && u.editedInReview) { S.review.confirm = 'regen'; render(); }
      else void reviewAction('regen');
      break;
    }
    case 'reviewRegenYes': void reviewAction('regen'); break;
    case 'reviewDelete': S.review.confirm = 'delete'; S.review.message = ''; render(); break;
    case 'reviewDeleteYes': void reviewAction('delete'); break;
    case 'reviewCancel': S.review.confirm = ''; render(); break;
    case 'approve': void reviewAction('approve'); break;
    case 'setOn': void toggleSet(a.dataset.id!, true); break;
    case 'setOffAsk': S.sets.confirm = a.dataset.id!; S.sets.message = ''; render(); break;
    case 'setOff': void toggleSet(a.dataset.id!, false); break;
    case 'setCancel': S.sets.confirm = ''; render(); break;
    case 'logout': void logout(); break;
    case 'pushToggle': void (S.push.subscribed ? pushOff() : pushOn()); break;
    case 'pushTest': void pushTest(); break;
    case 'retry': void sync.syncNow(); break;
  }
});

root.addEventListener('submit', (ev) => {
  const f = ev.target as HTMLFormElement;
  ev.preventDefault();
  switch (f.dataset.form) {
    case 'login': void submitLogin(f); break;
    case 'add': void submitUnit('add', f); break;
    case 'draft': void submitDraft(f); break;
    case 'edit': void submitUnit('edit', f); break;
    case 'settings': void submitSettings(f); break;
  }
});

// Черновики форм переживают перерисовку (например, при смене статуса синхронизации).
root.addEventListener('input', (ev) => {
  const t = ev.target as HTMLInputElement;
  if (t.id === 'search') {
    S.dict.q = t.value;
    const list = document.getElementById('dictList');
    if (list && sync.snapshot) list.innerHTML = dictList(view());
    return;
  }
  const f = t.form;
  if (!f) return;
  if (f.dataset.form === 'add') S.add.draft = readUnitForm(f);
  if (f.dataset.form === 'draft') {
    const fd = new FormData(f);
    S.draft.draft = { form: String(fd.get('form') ?? ''), sourceNote: String(fd.get('sourceNote') ?? '') };
  }
  if (f.dataset.form === 'edit') S.edit.draft = readUnitForm(f);
  if (f.dataset.form === 'settings') {
    S.settings.draft = readSettingsForm(f);
    if (t.name === 'poolOrder') render(); // подсветка выбранного варианта
  }
});

// ——— запуск ———

async function main() {
  const store = await openStore();
  sync = new Sync(store, httpApi);
  await sync.load();
  // Перерисовываем на изменения данных, но не посреди отправки формы.
  sync.onChange = () => {
    if (!S.add.busy && !S.draft.busy && !S.edit.busy && !S.settings.busy && !S.login.busy) render();
    scheduleAudioCache();
  };
  S.cachedAudio = await cachedAudioKeys().catch(() => new Set<string>());
  sync.onAuthRequired = requireLogin;
  window.addEventListener('hashchange', onRoute);
  window.addEventListener('online', () => { S.online = true; render(); });
  window.addEventListener('offline', () => { S.online = false; render(); });
  onVoicesChanged(render);
  S.route = parseHash();
  if (S.route.name === 'session') S.route = { name: 'today' };
  render();
  void refreshPush();
  startAutoSync(sync);
  pollGeneration();
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('service worker', e));
  });
  // Нажатие на уведомление при открытом приложении: на «Сегодня» с обновлёнными очередями.
  navigator.serviceWorker.addEventListener('message', (ev) => {
    if ((ev.data as { type?: string } | null)?.type !== 'open' || !sync) return;
    if (!S.needLogin) go('today');
    if (S.online) void sync.syncNow();
  });
}

void main();
